import { Box, Link, Text } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Receivable } from '@finley-technologies/finley-types';

import { Alert } from 'packages/common-frontend/components/Alert';
import {
  Metadata,
  useLatestAssignmentPipelineRunMetadataForLpGroupingContext,
} from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForLpGroupingProvider';
import { AssignmentTemplate } from 'packages/loanProduct/ui/AssignmentTemplate';

type StatusAlertProps = {
  status?: Receivable.Assignment.Pipeline.Status;
};
export const StatusAlert = ({ status }: StatusAlertProps) => {
  switch (status) {
    case 'incomplete':
      return (
        <Alert status="warning" isClosable={false}>
          <Text>
            Your recent upload is incomplete. Deliver new tapes via existing
            workflow or{' '}
            <Link as={RouterLink} to="/manual-uploader">
              Manual Uploader
            </Link>
            .
          </Text>
        </Alert>
      );
    case 'noPipelineRun':
      return (
        <Alert status="warning" isClosable={false}>
          <Text>
            The credit agreement or loan tape dependency graph has been updated.
            Deliver tapes via existing workflow or{' '}
            <Link as={RouterLink} to="/manual-uploader">
              Manual Uploader
            </Link>
            &nbsp;to proceed with assignment.
          </Text>
        </Alert>
      );
    case 'assigned':
      return (
        <Alert status="warning" isClosable={false}>
          <Text>
            The latest tape was assigned. Deliver new tapes via existing
            workflow or{' '}
            <Link as={RouterLink} to="/manual-uploader">
              Manual Uploader
            </Link>
            &nbsp;to proceed with assignment.
          </Text>
        </Alert>
      );
    case 'success':
      return (
        <Alert status="success" isClosable={false}>
          <Text>The proposed owners have been successfully updated.</Text>
        </Alert>
      );
    case 'error':
      return (
        <Alert status="error" isClosable={false}>
          <Text>
            Your recent upload encountered an error. Please visit the{' '}
            <Link as={RouterLink} to="/manual-uploader">
              Manual Uploader
            </Link>{' '}
            tab to learn more.
          </Text>
        </Alert>
      );
    default:
    case 'inProgress':
      return (
        <Alert status="warning" isClosable={false}>
          <Text>
            Your tapes are processing. You will be notified by email when tapes
            are ready to assign.
          </Text>
        </Alert>
      );
  }
};

type FallbackAssignmentTemplateProps = {
  status?: Receivable.Assignment.Pipeline.Status;
  children?: React.ReactNode;
};
export const FallbackAssignmentTemplate = ({
  children,
  status,
}: FallbackAssignmentTemplateProps) => {
  return (
    <Box height="100%">
      <AssignmentTemplate>
        <StatusAlert status={status} />
        {children}
      </AssignmentTemplate>
    </Box>
  );
};

export const CheckLatestPipelineRunStatus = ({
  children,
}: {
  children: (input: {
    StatusAlert: typeof StatusAlert;
    FallbackAssignmentTemplate: typeof FallbackAssignmentTemplate;
    metadata: Metadata;
  }) => React.ReactElement;
}) => {
  const {
    metadata,
  } = useLatestAssignmentPipelineRunMetadataForLpGroupingContext();

  // !TODO @eldarshamukhamedov Components should not be declared inside other components
  type FallbackAssignmentTemplateComponentProps = {
    status?: Receivable.Assignment.Pipeline.Status;
    children?: React.ReactNode;
  };
  const FallbackAssignmentTemplateComponent = useCallback(
    ({
      status,
      children: fallbackTemplateChildren,
    }: FallbackAssignmentTemplateComponentProps) => {
      return (
        <FallbackAssignmentTemplate status={status || metadata?.status}>
          {fallbackTemplateChildren}
        </FallbackAssignmentTemplate>
      );
    },
    [metadata],
  );

  return (
    <Box height="100%">
      {children({
        StatusAlert,
        FallbackAssignmentTemplate: FallbackAssignmentTemplateComponent,
        metadata,
      })}
    </Box>
  );
};
